// ══════════════════════════════════════════════════════════════════════════════
// ApiEsternePage.tsx — Funzioni Sviluppatore → API esterne
// Per società: applicazioni chiamanti, API autorizzate (interruttori), chiavi
// (mostrate una volta), ultime chiamate. Società dalla topbar.
// Design: docs/superpowers/specs/2026-09-15-api-esterne-design.md
// ══════════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { showToast } from '../../components/Toast';
import { apiFetch, apiDownload } from '../../lib/api';
import { useSocieta } from '../../context/SocietaContext';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './ApiEsternePage.api';
import BACKEND_URL from '../../config.js';
import {
  Applicazione, ApplicazioneDettaglio, Chiave, ChiaveGenerata, EndpointCatalogo, LogChiamata, VoceCatalogo,
  slugDaNome, raggruppaCatalogo, etichettaBreve, badgeStatoChiave, badgeEsito, fmtDataOra, fmtData,
  urlCompleta, urlEsempio, esempioCurl,
} from './apiEsterne';

const BASE = '/api/dev/api-esterne';

interface FormApp { codice: string; nome: string; descrizione: string; referente: string; note: string }
const FORM_APP_VUOTO: FormApp = { codice: '', nome: '', descrizione: '', referente: '', note: '' };

export default function ApiEsternePage() {
  const { societaAttiva } = useSocieta();
  const { can } = usePermesso();
  const canEdit = can('dev.api_esterne', 'edit');
  const societaId = societaAttiva?.id ?? null;

  const [catalogo, setCatalogo] = useState<VoceCatalogo[]>([]);
  // Base URL pubblica per i chiamanti esterni (dal backend); in attesa, quella del frontend
  const [baseUrl, setBaseUrl] = useState<string>(BACKEND_URL);
  const [applicazioni, setApplicazioni] = useState<Applicazione[]>([]);
  const [selezionataId, setSelezionataId] = useState<number | null>(null);
  const [dettaglio, setDettaglio] = useState<ApplicazioneDettaglio | null>(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [busy, setBusy] = useState(false);

  // Modali
  const [modaleNuova, setModaleNuova] = useState(false);
  const [formNuova, setFormNuova] = useState<FormApp>(FORM_APP_VUOTO);
  const [codiceManuale, setCodiceManuale] = useState(false);
  const [formDati, setFormDati] = useState<FormApp>(FORM_APP_VUOTO);
  const [modaleChiave, setModaleChiave] = useState(false);
  const [formChiave, setFormChiave] = useState({ nome: '', scadenzaAt: '' });
  const [chiaveGenerata, setChiaveGenerata] = useState<ChiaveGenerata | null>(null);
  const [copiata, setCopiata] = useState(false);

  // Mini-swagger: endpoint del catalogo aperto nella modale di dettaglio
  const [endpointAperto, setEndpointAperto] = useState<{ voce: VoceCatalogo; endpoint: EndpointCatalogo } | null>(null);

  // PDF con le sole API abilitate all'applicazione, da consegnare al chiamante
  const scaricaDocumentazione = async () => {
    if (!dettaglio) return;
    setBusy(true);
    try {
      // @api GET /api/dev/api-esterne/applicazioni/1/documentazione.pdf -> application/pdf
      await apiDownload(`${BASE}/applicazioni/${dettaglio.id}/documentazione.pdf`, `api-bop-${dettaglio.codice}.pdf`);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const copiaTesto = async (testo: string, cosa: string) => {
    try {
      await navigator.clipboard.writeText(testo);
      showToast(`${cosa} copiato negli appunti`);
    } catch {
      showToast('Copia automatica non disponibile: seleziona e copia il testo', 'warning');
    }
  };

  // ── Caricamento ─────────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        // @api GET /api/dev/api-esterne/catalogo -> {"success":true,"baseUrl":"https://back-bop.pianinvest.it","data":[{"codice":"nominativi.lettura","etichetta":"Nominativi (lettura)","gruppo":"Anagrafiche","descrizione":"...","endpoints":[{"metodo":"GET","path":"/api/ext/nominativi","titolo":"Lista nominativi","descrizione":"...","parametri":[],"risposta":{"tipo":"lista","campi":[]},"errori":[],"esempioRisposta":{}}]}]}
        const res = await apiFetch<{ data: VoceCatalogo[]; baseUrl?: string }>(`${BASE}/catalogo`);
        setCatalogo(res.data);
        if (res.baseUrl) setBaseUrl(res.baseUrl);
      } catch (e) { setErrore((e as Error).message); }
    })();
  }, []);

  const caricaApplicazioni = useCallback(async () => {
    if (!societaId) { setApplicazioni([]); setSelezionataId(null); return; }
    setLoading(true); setErrore('');
    try {
      // @api GET /api/dev/api-esterne/applicazioni?societaId=1 -> {"success":true,"data":[{"id":1,"societaId":1,"codice":"crm","nome":"CRM","descrizione":null,"referente":null,"attiva":true,"note":null,"apiAbilitate":["nominativi.lettura"],"chiavi":[],"chiaviAttive":0}]}
      const res = await apiFetch<{ data: Applicazione[] }>(`${BASE}/applicazioni?societaId=${societaId}`);
      setApplicazioni(res.data);
      setSelezionataId(prev => (prev && res.data.some(a => a.id === prev)) ? prev : (res.data[0]?.id ?? null));
    } catch (e) { setErrore((e as Error).message); }
    finally { setLoading(false); }
  }, [societaId]);

  const caricaDettaglio = useCallback(async (id: number) => {
    try {
      // @api GET /api/dev/api-esterne/applicazioni/1 -> {"success":true,"data":{"id":1,"codice":"crm","nome":"CRM","attiva":true,"apiAbilitate":["nominativi.lettura"],"chiavi":[{"id":1,"nome":"Prod","prefisso":"bop_a1b2c3d4","stato":"ATTIVA","statoEffettivo":"ATTIVA","scadenzaAt":null,"ultimoUtilizzoAt":null,"numeroChiamate":0}],"chiaviAttive":1,"ultimeChiamate":[]}}
      const res = await apiFetch<{ data: ApplicazioneDettaglio }>(`${BASE}/applicazioni/${id}`);
      setDettaglio(res.data);
      setFormDati({
        codice: res.data.codice, nome: res.data.nome, descrizione: res.data.descrizione ?? '',
        referente: res.data.referente ?? '', note: res.data.note ?? '',
      });
    } catch (e) { setErrore((e as Error).message); setDettaglio(null); }
  }, []);

  useEffect(() => { caricaApplicazioni(); }, [caricaApplicazioni]);
  useEffect(() => { if (selezionataId) caricaDettaglio(selezionataId); else setDettaglio(null); }, [selezionataId, caricaDettaglio]);

  // Aggiorna la riga in lista con il DTO tornato dal backend (chip e contatori).
  const aggiornaInLista = (app: Applicazione) =>
    setApplicazioni(prev => prev.map(a => (a.id === app.id ? { ...a, ...app } : a)));

  // ── Azioni ──────────────────────────────────────────────────────────────────

  const creaApplicazione = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societaId) return;
    setBusy(true);
    try {
      // @api POST /api/dev/api-esterne/applicazioni {"societaId":1,"codice":"crm","nome":"CRM","descrizione":"","referente":"","note":""} -> {"success":true,"data":{"id":1,"codice":"crm","nome":"CRM","apiAbilitate":[],"chiavi":[],"chiaviAttive":0}}
      const res = await apiFetch<{ data: Applicazione }>(`${BASE}/applicazioni`, {
        method: 'POST', body: JSON.stringify({ societaId, ...formNuova }),
      });
      showToast('Applicazione creata');
      setModaleNuova(false); setFormNuova(FORM_APP_VUOTO); setCodiceManuale(false);
      await caricaApplicazioni();
      setSelezionataId(res.data.id);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const salvaDati = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dettaglio) return;
    setBusy(true);
    try {
      // @api PUT /api/dev/api-esterne/applicazioni/1 {"nome":"CRM","descrizione":"","referente":"","note":""} -> {"success":true,"data":{"id":1,"nome":"CRM","attiva":true}}
      const res = await apiFetch<{ data: Applicazione }>(`${BASE}/applicazioni/${dettaglio.id}`, {
        method: 'PUT', body: JSON.stringify({ nome: formDati.nome, descrizione: formDati.descrizione, referente: formDati.referente, note: formDati.note }),
      });
      setDettaglio(d => (d ? { ...d, ...res.data } : d)); aggiornaInLista(res.data);
      showToast('Dati salvati');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const cambiaAttiva = async () => {
    if (!dettaglio) return;
    const nuova = !dettaglio.attiva;
    if (!nuova && !window.confirm('Disattivare l\'applicazione? Tutte le sue chiavi smetteranno di funzionare subito.')) return;
    setBusy(true);
    try {
      // @api PUT /api/dev/api-esterne/applicazioni/1 {"attiva":false} -> {"success":true,"data":{"id":1,"attiva":false}}
      const res = await apiFetch<{ data: Applicazione }>(`${BASE}/applicazioni/${dettaglio.id}`, {
        method: 'PUT', body: JSON.stringify({ attiva: nuova }),
      });
      setDettaglio(d => (d ? { ...d, ...res.data } : d)); aggiornaInLista(res.data);
      showToast(nuova ? 'Applicazione riattivata' : 'Applicazione disattivata');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const toggleApi = async (codice: string) => {
    if (!dettaglio) return;
    const attuali = dettaglio.apiAbilitate;
    const codici = attuali.includes(codice) ? attuali.filter(c => c !== codice) : [...attuali, codice];
    setBusy(true);
    try {
      // @api PUT /api/dev/api-esterne/applicazioni/1/api {"codici":["nominativi.lettura"]} -> {"success":true,"data":{"id":1,"apiAbilitate":["nominativi.lettura"]}}
      const res = await apiFetch<{ data: Applicazione }>(`${BASE}/applicazioni/${dettaglio.id}/api`, {
        method: 'PUT', body: JSON.stringify({ codici }),
      });
      setDettaglio(d => (d ? { ...d, ...res.data } : d)); aggiornaInLista(res.data);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const generaChiave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dettaglio) return;
    setBusy(true);
    try {
      // @api POST /api/dev/api-esterne/applicazioni/1/chiavi {"nome":"Produzione","scadenzaAt":null} -> {"success":true,"data":{"id":1,"nome":"Produzione","prefisso":"bop_a1b2c3d4","stato":"ATTIVA","statoEffettivo":"ATTIVA","chiave":"bop_...40hex"}}
      const res = await apiFetch<{ data: ChiaveGenerata }>(`${BASE}/applicazioni/${dettaglio.id}/chiavi`, {
        method: 'POST', body: JSON.stringify({ nome: formChiave.nome, scadenzaAt: formChiave.scadenzaAt || null }),
      });
      setChiaveGenerata(res.data); setCopiata(false);
      setModaleChiave(false); setFormChiave({ nome: '', scadenzaAt: '' });
      await caricaDettaglio(dettaglio.id); await caricaApplicazioni();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const revocaChiave = async (c: Chiave) => {
    if (!dettaglio) return;
    if (!window.confirm(`Revocare la chiave ${c.prefisso}… (${c.nome})? Il chiamante riceverà 401 da subito.`)) return;
    setBusy(true);
    try {
      // @api POST /api/dev/api-esterne/applicazioni/1/chiavi/1/revoca -> {"success":true,"data":{"id":1,"stato":"REVOCATA","statoEffettivo":"REVOCATA"}}
      await apiFetch(`${BASE}/applicazioni/${dettaglio.id}/chiavi/${c.id}/revoca`, { method: 'POST' });
      showToast('Chiave revocata');
      await caricaDettaglio(dettaglio.id); await caricaApplicazioni();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };

  const aggiornaChiamate = async () => {
    if (!dettaglio) return;
    try {
      // @api GET /api/dev/api-esterne/applicazioni/1/log?limit=20 -> {"success":true,"data":[{"id":1,"createdAt":"2026-09-15T10:00:00.000Z","prefisso":"bop_a1b2c3d4","codiceApi":"nominativi.lettura","metodo":"GET","path":"/api/ext/nominativi","statusCode":200,"durataMs":12,"ip":"1.2.3.4","esito":"OK","errore":null}]}
      const res = await apiFetch<{ data: LogChiamata[] }>(`${BASE}/applicazioni/${dettaglio.id}/log?limit=20`);
      setDettaglio(d => (d ? { ...d, ultimeChiamate: res.data } : d));
    } catch (e) { showToast((e as Error).message, 'error'); }
  };

  const copiaChiave = async () => {
    if (!chiaveGenerata) return;
    try {
      await navigator.clipboard.writeText(chiaveGenerata.chiave);
      setCopiata(true); showToast('Chiave copiata negli appunti');
    } catch {
      showToast('Copia automatica non disponibile: seleziona e copia il testo', 'warning');
    }
  };

  // ── Derivati ────────────────────────────────────────────────────────────────

  const gruppi = useMemo(() => raggruppaCatalogo(catalogo), [catalogo]);
  const etichette = useMemo(() => Object.fromEntries(catalogo.map(v => [v.codice, etichettaBreve(v.etichetta, v.codice)])), [catalogo]);
  const orfane = useMemo(() => (dettaglio?.apiAbilitate ?? []).filter(c => !catalogo.some(v => v.codice === c)), [dettaglio, catalogo]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">API esterne</h1>
          <p className="bop-page-subtitle">
            {societaAttiva
              ? `Applicazioni esterne autorizzate a chiamare BOP per ${societaAttiva.nome}: chiavi e API abilitate.`
              : 'Seleziona una società nella barra in alto per gestire le sue applicazioni esterne.'}
          </p>
        </div>
        {societaId && canEdit && (
          <button type="button" className="bop-btn bop-btn-primary" onClick={() => setModaleNuova(true)}>Nuova applicazione</button>
        )}
      </div>

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      {!societaId ? (
        <div className="bop-empty-state">
          <div className="bop-empty-state__title">Nessuna società selezionata</div>
          <div className="bop-empty-state__text">Le API Key sono per società: scegline una nella barra in alto.</div>
        </div>
      ) : (
        <div className="bop-api-esterne">
          {/* ── Colonna sinistra: applicazioni ── */}
          <div className="bop-api-esterne__colonna">
            <div className="bop-card">
              <div className="bop-card__header">
                <h2 className="bop-card__title">Applicazioni ({applicazioni.length})</h2>
              </div>
              {loading ? (
                <div className="bop-card__body bop-text-muted">Caricamento…</div>
              ) : applicazioni.length === 0 ? (
                <div className="bop-empty-state">
                  <div className="bop-empty-state__title">Nessuna applicazione esterna per questa società</div>
                  <div className="bop-empty-state__text">Crea la prima con "Nuova applicazione".</div>
                </div>
              ) : (
                <ul className="bop-api-esterne__lista">
                  {applicazioni.map(a => (
                    <li
                      key={a.id}
                      className={`bop-api-esterne__app${a.id === selezionataId ? ' bop-api-esterne__app--selezionata' : ''}${a.attiva ? '' : ' bop-api-esterne__app--disattivata'}`}
                      onClick={() => setSelezionataId(a.id)}
                    >
                      <div className="bop-api-esterne__app-riga">
                        <span className="bop-api-esterne__app-nome">{a.nome}</span>
                        <span className={a.attiva ? 'bop-badge bop-badge--success' : 'bop-badge bop-badge--neutral'}>{a.attiva ? 'Attiva' : 'Disattivata'}</span>
                      </div>
                      <div className="bop-api-esterne__app-meta">
                        <span className="bop-text-mono">{a.codice}</span> · {a.chiaviAttive} {a.chiaviAttive === 1 ? 'chiave attiva' : 'chiavi attive'}
                      </div>
                      <div className="bop-api-esterne__chips">
                        {a.apiAbilitate.length === 0
                          ? <span className="bop-api-esterne__chip bop-api-esterne__chip--vuoto">nessuna API</span>
                          : a.apiAbilitate.map(c => <span key={c} className="bop-api-esterne__chip">{etichette[c] ?? c}</span>)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* ── Colonna destra: applicazione selezionata ── */}
          <div className="bop-api-esterne__colonna">
            {!dettaglio ? (
              <div className="bop-card"><div className="bop-card__body bop-text-muted">Seleziona un'applicazione a sinistra.</div></div>
            ) : (
              <>
                {/* Dati applicazione */}
                <div className="bop-card">
                  <div className="bop-card__header">
                    <h2 className="bop-card__title">{dettaglio.nome} <span className="bop-text-mono bop-text-muted">{dettaglio.codice}</span></h2>
                    <div className="bop-api-esterne__azioni">
                      <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" disabled={busy}
                        title="PDF con le API abilitate a questa applicazione, da consegnare al chiamante"
                        onClick={scaricaDocumentazione}>Scarica documentazione (PDF)</button>
                      {canEdit && (
                        <button type="button" className={`bop-switch${dettaglio.attiva ? ' bop-switch--on' : ''}`} disabled={busy}
                          onClick={cambiaAttiva} aria-label={dettaglio.attiva ? 'Disattiva applicazione' : 'Riattiva applicazione'} title={dettaglio.attiva ? 'Attiva' : 'Disattivata'} />
                      )}
                    </div>
                  </div>
                  <form className="bop-card__body" onSubmit={salvaDati}>
                    <div className="bop-api-esterne__form">
                      <div className="bop-form-group">
                        <label className="bop-label" htmlFor="ae-nome">Nome</label>
                        <input id="ae-nome" className="bop-input" value={formDati.nome} disabled={!canEdit} onChange={e => setFormDati(f => ({ ...f, nome: e.target.value }))} required />
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label" htmlFor="ae-referente">Referente</label>
                        <input id="ae-referente" className="bop-input" value={formDati.referente} disabled={!canEdit} onChange={e => setFormDati(f => ({ ...f, referente: e.target.value }))} />
                      </div>
                      <div className="bop-form-group bop-api-esterne__form--intera">
                        <label className="bop-label" htmlFor="ae-descrizione">Descrizione</label>
                        <input id="ae-descrizione" className="bop-input" value={formDati.descrizione} disabled={!canEdit} onChange={e => setFormDati(f => ({ ...f, descrizione: e.target.value }))} />
                      </div>
                      <div className="bop-form-group bop-api-esterne__form--intera">
                        <label className="bop-label" htmlFor="ae-note">Note</label>
                        <textarea id="ae-note" className="bop-input" rows={2} value={formDati.note} disabled={!canEdit} onChange={e => setFormDati(f => ({ ...f, note: e.target.value }))} />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="bop-api-esterne__azioni">
                        <button type="submit" className="bop-btn bop-btn-primary" disabled={busy}>Salva</button>
                      </div>
                    )}
                  </form>
                </div>

                {/* API autorizzate */}
                <div className="bop-card">
                  <div className="bop-card__header">
                    <h2 className="bop-card__title">API autorizzate</h2>
                    <span className="bop-text-muted">{dettaglio.apiAbilitate.filter(c => catalogo.some(v => v.codice === c)).length} di {catalogo.length} abilitate</span>
                  </div>
                  <div className="bop-card__body">
                    {gruppi.map(g => (
                      <div key={g.gruppo}>
                        <div className="bop-api-esterne__gruppo-titolo">{g.gruppo}</div>
                        {g.voci.map(v => {
                          const on = dettaglio.apiAbilitate.includes(v.codice);
                          return (
                            <div key={v.codice} className="bop-api-esterne__voce">
                              {canEdit ? (
                                <button type="button" className={`bop-switch${on ? ' bop-switch--on' : ''}`} disabled={busy}
                                  onClick={() => toggleApi(v.codice)} aria-label={`${on ? 'Disabilita' : 'Abilita'} ${v.etichetta}`} />
                              ) : (
                                <span className={on ? 'bop-badge bop-badge--success' : 'bop-badge bop-badge--neutral'}>{on ? 'Abilitata' : 'Non abilitata'}</span>
                              )}
                              <div className="bop-api-esterne__voce-testo">
                                <span className="bop-api-esterne__voce-etichetta">{v.etichetta} <span className="bop-text-mono bop-text-muted">{v.codice}</span></span>
                                <span className="bop-api-esterne__voce-descrizione">{v.descrizione}</span>
                                {v.endpoints.map(e => (
                                  <button key={e.metodo + e.path} type="button"
                                    className="bop-api-esterne__endpoint bop-api-esterne__endpoint--link"
                                    title="Dettagli della chiamata e URL completa"
                                    aria-label={`Dettagli di ${e.metodo} ${e.path}`}
                                    onClick={() => setEndpointAperto({ voce: v, endpoint: e })}>
                                    {e.metodo} {e.path}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    {orfane.length > 0 && (
                      <div>
                        <div className="bop-api-esterne__gruppo-titolo">Non più nel catalogo</div>
                        {orfane.map(c => (
                          <div key={c} className="bop-api-esterne__voce bop-api-esterne__voce--orfana">
                            <div className="bop-api-esterne__voce-testo">
                              <span className="bop-api-esterne__voce-etichetta bop-text-mono">{c}</span>
                              <span className="bop-api-esterne__voce-descrizione">Abilitata ma senza endpoint attivo: non autorizza nulla.</span>
                            </div>
                            {canEdit && <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" disabled={busy} onClick={() => toggleApi(c)}>Rimuovi</button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Chiavi */}
                <div className="bop-card">
                  <div className="bop-card__header">
                    <h2 className="bop-card__title">Chiavi</h2>
                    {canEdit && <button type="button" className="bop-btn bop-btn-primary bop-btn-sm" disabled={busy || !dettaglio.attiva} onClick={() => setModaleChiave(true)}>Genera chiave</button>}
                  </div>
                  {dettaglio.chiavi.length === 0 ? (
                    <div className="bop-card__body bop-text-muted">Nessuna chiave. Generane una per far chiamare BOP a questa applicazione.</div>
                  ) : (
                    <div className="bop-table-wrapper">
                      <table className="bop-table">
                        <thead>
                          <tr><th>Prefisso</th><th>Nome</th><th>Stato</th><th>Scadenza</th><th>Ultimo utilizzo</th><th className="bop-text-right">Chiamate</th><th></th></tr>
                        </thead>
                        <tbody>
                          {dettaglio.chiavi.map(c => {
                            const b = badgeStatoChiave(c.statoEffettivo);
                            return (
                              <tr key={c.id}>
                                <td className="bop-text-mono">{c.prefisso}…</td>
                                <td>{c.nome}</td>
                                <td><span className={b.classe}>{b.testo}</span></td>
                                <td>{fmtData(c.scadenzaAt)}</td>
                                <td>{fmtDataOra(c.ultimoUtilizzoAt)}</td>
                                <td className="bop-text-right">{c.numeroChiamate}</td>
                                <td className="bop-text-right">
                                  {canEdit && c.stato === 'ATTIVA' && (
                                    <button type="button" className="bop-btn bop-btn-danger bop-btn-sm" disabled={busy} onClick={() => revocaChiave(c)}>Revoca</button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Ultime chiamate */}
                <div className="bop-card">
                  <div className="bop-card__header">
                    <h2 className="bop-card__title">Ultime chiamate</h2>
                    <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={aggiornaChiamate}>Aggiorna</button>
                  </div>
                  {dettaglio.ultimeChiamate.length === 0 ? (
                    <div className="bop-card__body bop-text-muted">Nessuna chiamata registrata.</div>
                  ) : (
                    <div className="bop-table-wrapper">
                      <table className="bop-table">
                        <thead>
                          <tr><th>Data/ora</th><th>Chiave</th><th>API</th><th>Richiesta</th><th>Esito</th><th className="bop-text-right">ms</th><th>IP</th></tr>
                        </thead>
                        <tbody>
                          {dettaglio.ultimeChiamate.map(l => {
                            const b = badgeEsito(l.esito);
                            return (
                              <tr key={l.id}>
                                <td>{fmtDataOra(l.createdAt)}</td>
                                <td className="bop-text-mono">{l.prefisso ?? '—'}</td>
                                <td>{l.codiceApi ? (etichette[l.codiceApi] ?? l.codiceApi) : 'whoami'}</td>
                                <td className="bop-text-mono">{l.metodo} {l.path}</td>
                                <td><span className={b.classe} title={l.errore ?? ''}>{b.testo} {l.statusCode}</span></td>
                                <td className="bop-text-right">{l.durataMs}</td>
                                <td className="bop-text-mono">{l.ip ?? '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modale nuova applicazione ── */}
      {modaleNuova && (
        <div className="bop-modal-overlay" onClick={() => setModaleNuova(false)}>
          <div className="bop-modal bop-modal--md" onClick={e => e.stopPropagation()}>
            <form onSubmit={creaApplicazione}>
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">Nuova applicazione esterna</h3>
                <button type="button" className="bop-modal__close" onClick={() => setModaleNuova(false)} aria-label="Chiudi">×</button>
              </div>
              <div className="bop-modal__body">
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="na-nome">Nome</label>
                  <input id="na-nome" className="bop-input" value={formNuova.nome} autoFocus required
                    onChange={e => setFormNuova(f => ({ ...f, nome: e.target.value, codice: codiceManuale ? f.codice : slugDaNome(e.target.value) }))} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="na-codice">Codice (minuscole, cifre, trattini)</label>
                  <input id="na-codice" className="bop-input bop-text-mono" value={formNuova.codice} required pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={40}
                    onChange={e => { setCodiceManuale(true); setFormNuova(f => ({ ...f, codice: e.target.value })); }} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="na-descrizione">Descrizione</label>
                  <input id="na-descrizione" className="bop-input" value={formNuova.descrizione} onChange={e => setFormNuova(f => ({ ...f, descrizione: e.target.value }))} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="na-referente">Referente</label>
                  <input id="na-referente" className="bop-input" value={formNuova.referente} onChange={e => setFormNuova(f => ({ ...f, referente: e.target.value }))} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="na-note">Note</label>
                  <textarea id="na-note" className="bop-input" rows={2} value={formNuova.note} onChange={e => setFormNuova(f => ({ ...f, note: e.target.value }))} />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button type="button" className="bop-btn bop-btn-secondary" onClick={() => setModaleNuova(false)}>Annulla</button>
                <button type="submit" className="bop-btn bop-btn-primary" disabled={busy}>Crea</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modale genera chiave ── */}
      {modaleChiave && dettaglio && (
        <div className="bop-modal-overlay" onClick={() => setModaleChiave(false)}>
          <div className="bop-modal bop-modal--sm" onClick={e => e.stopPropagation()}>
            <form onSubmit={generaChiave}>
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">Genera chiave per {dettaglio.nome}</h3>
                <button type="button" className="bop-modal__close" onClick={() => setModaleChiave(false)} aria-label="Chiudi">×</button>
              </div>
              <div className="bop-modal__body">
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="gk-nome">Nome (es. "Produzione 2026")</label>
                  <input id="gk-nome" className="bop-input" value={formChiave.nome} autoFocus required onChange={e => setFormChiave(f => ({ ...f, nome: e.target.value }))} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="gk-scadenza">Scadenza (opzionale)</label>
                  <input id="gk-scadenza" type="date" className="bop-input" value={formChiave.scadenzaAt} onChange={e => setFormChiave(f => ({ ...f, scadenzaAt: e.target.value }))} />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button type="button" className="bop-btn bop-btn-secondary" onClick={() => setModaleChiave(false)}>Annulla</button>
                <button type="submit" className="bop-btn bop-btn-primary" disabled={busy}>Genera</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modale chiave generata (mostrata una sola volta) ── */}
      {chiaveGenerata && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--md">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Chiave generata: {chiaveGenerata.nome}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-alert bop-alert--danger">Questa chiave non sarà più visibile dopo la chiusura: copiala ora e consegnala al referente dell'applicazione.</div>
              <div className="bop-api-esterne__chiave-box">
                <code>{chiaveGenerata.chiave}</code>
                <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={copiaChiave}>{copiata ? 'Copiata' : 'Copia'}</button>
              </div>
              <p className="bop-text-muted">Header da usare: <span className="bop-text-mono">x-api-key</span>. Collaudo: <span className="bop-text-mono">GET /api/ext/whoami</span>.</p>
            </div>
            <div className="bop-modal__footer">
              <button type="button" className="bop-btn bop-btn-primary" onClick={() => setChiaveGenerata(null)}>Ho copiato la chiave, chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale dettaglio endpoint (mini-swagger) ── */}
      {endpointAperto && (() => {
        const { voce, endpoint: e } = endpointAperto;
        const url = urlCompleta(baseUrl, e.path);
        const curl = esempioCurl(baseUrl, e);
        const jsonEsempio = JSON.stringify(e.esempioRisposta, null, 2);
        return (
          <div className="bop-modal-overlay" onClick={() => setEndpointAperto(null)}>
            <div className="bop-modal bop-modal--lg" onClick={ev => ev.stopPropagation()}>
              <div className="bop-modal__header">
                <h3 className="bop-modal__title"><span className="bop-api-esterne__metodo">{e.metodo}</span><span className="bop-text-mono">{e.path}</span></h3>
                <button type="button" className="bop-modal__close" onClick={() => setEndpointAperto(null)} aria-label="Chiudi">×</button>
              </div>
              <div className="bop-modal__body bop-api-esterne__doc">
                <div>
                  <p className="bop-api-esterne__doc-sottotitolo">{e.titolo} · {voce.etichetta} <span className="bop-text-mono bop-text-muted">{voce.codice}</span></p>
                  <p className="bop-api-esterne__doc-testo">{e.descrizione}</p>
                </div>

                <div>
                  <p className="bop-api-esterne__doc-titolo">URL completa</p>
                  <div className="bop-api-esterne__chiave-box">
                    <code>{url}</code>
                    <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => copiaTesto(url, 'URL')}>Copia</button>
                  </div>
                  <p className="bop-api-esterne__doc-testo">Autenticazione: header <span className="bop-text-mono">x-api-key</span> con una chiave attiva dell'applicazione, che deve avere abilitata l'API <span className="bop-text-mono">{voce.codice}</span>.</p>
                </div>

                <div>
                  <p className="bop-api-esterne__doc-titolo">Parametri</p>
                  <div className="bop-table-wrapper">
                    <table className="bop-table">
                      <thead><tr><th>Nome</th><th>Dove</th><th>Tipo</th><th>Obbligatorio</th><th>Descrizione</th></tr></thead>
                      <tbody>
                        {e.parametri.map(p => (
                          <tr key={p.in + p.nome}>
                            <td className="bop-text-mono">{p.nome}</td>
                            <td>{p.in === 'path' ? 'path' : p.in === 'body' ? 'corpo JSON' : 'query string'}</td>
                            <td className="bop-text-mono">{p.tipo}</td>
                            <td>{p.obbligatorio ? 'sì' : 'no'}</td>
                            <td>{p.descrizione} {p.esempio && <span className="bop-text-muted">es. <span className="bop-text-mono">{p.esempio}</span></span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <p className="bop-api-esterne__doc-titolo">Risposta {e.risposta.tipo === 'lista' ? '(lista paginata: success, data[], page, pageSize, total)' : '(dettaglio: success, data)'}</p>
                  <div className="bop-table-wrapper">
                    <table className="bop-table">
                      <thead><tr><th>Campo</th><th>Tipo</th><th>Descrizione</th></tr></thead>
                      <tbody>
                        {e.risposta.campi.map(c => (
                          <tr key={c.nome}>
                            <td className="bop-text-mono">{c.nome}</td>
                            <td className="bop-text-mono">{c.tipo}</td>
                            <td>{c.descrizione}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <p className="bop-api-esterne__doc-titolo">Errori</p>
                  <ul className="bop-api-esterne__errori">
                    {e.errori.map(er => <li key={er.stato}><span className="bop-text-mono">{er.stato}</span> — {er.quando}</li>)}
                  </ul>
                </div>

                <div>
                  <p className="bop-api-esterne__doc-titolo">Esempio di chiamata</p>
                  <div className="bop-api-esterne__chiave-box">
                    <code>{curl}</code>
                    <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => copiaTesto(curl, 'Comando')}>Copia</button>
                  </div>
                </div>

                <div>
                  <p className="bop-api-esterne__doc-titolo">Esempio di risposta</p>
                  <pre className="bop-api-esterne__pre">{jsonEsempio}</pre>
                </div>
              </div>
              <div className="bop-modal__footer">
                <button type="button" className="bop-btn bop-btn-primary" onClick={() => setEndpointAperto(null)}>Chiudi</button>
              </div>
            </div>
          </div>
        );
      })()}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
